import React, { useState } from 'react';
import { useLanguage } from '../i18n/LanguageContext';

export type Treasure = {
  id: number;
  name: string;
  amountTon: number;
  payoutTon: number;
  roiPercent: number;
  cycleHours: number;
  earnedTon?: number;
  claimsCount?: number; // Количество собранных наград
  unlocked: boolean;
  unlockProgress?: number; // Текущий прогресс claim'ов для разблокировки
};

type Props = {
  treasure: Treasure;
  onBuy?: (id: number) => void;
  hideAction?: boolean;
  previousTreasureName?: string; // Название предыдущего сундука для разблокировки
};

const TreasureCard = ({ treasure, onBuy, hideAction, previousTreasureName }: Props) => {
  const { id, name, amountTon, payoutTon, roiPercent, cycleHours, earnedTon = 0, claimsCount = 0, unlocked, unlockProgress = 0 } = treasure;
  const { t } = useLanguage();
  const [imageError, setImageError] = useState(false);
  
  // Требуемое количество claim'ов для разблокировки каждого сундука
  const requiredClaims: Record<number, number> = {
    2: 1,
    3: 2,
    4: 3,
    5: 3,
    6: 3,
    7: 4,
    8: 4,
    9: 4,
    10: 2,
    11: 2,
    12: 2
  };
  
  const required = requiredClaims[id] || 3;

  return (
    <div className={`card relative overflow-hidden ${imageError ? 'opacity-10' : ''}`}>
      <div className="flex items-center gap-3">
        <div className="h-20 w-20 flex-shrink-0 flex items-center justify-center">
          {!imageError ? (
            <img 
              src={`/assets/treasure/${id}.png`} 
              alt={name}
              className="object-contain"
              style={{ 
                width: '80px',
                height: '80px',
                imageRendering: 'pixelated'
              }}
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="text-5xl">🧰</div>
          )}
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between gap-2">
            <div className="text-gray-900 font-semibold text-lg">{name}</div>
            <div className="text-xs font-semibold text-green-600 whitespace-nowrap">
              {t.allTimeProfit}: +{earnedTon.toFixed(4)}
            </div>
          </div>
          <div className="text-xs text-gray-500">{t.cycle}: {cycleHours}h • ROI: +{roiPercent}%</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
        <div>
          <div className="text-gray-500">{t.cost}</div>
          <div className="font-semibold">{amountTon} TON</div>
        </div>
        <div>
          <div className="text-gray-500">{t.payout}</div>
          <div className="font-semibold">{payoutTon} TON</div>
        </div>
      </div>

      {/* Статистика Claims */}
      {claimsCount > 0 && (
        <div className="mt-2 text-xs text-gray-600 text-center bg-green-50 rounded py-1">
          🎁 {claimsCount} {claimsCount === 1 ? 'claim' : 'claims'} collected
        </div>
      )}

      {/* Прогресс разблокировки для закрытых сундуков */}
      {!unlocked && !hideAction && id > 1 && id <= 12 && (
        <div className="mt-3 p-3 bg-blue-100 border border-blue-300 rounded-lg">
          <div className="text-sm font-semibold text-blue-700 mb-2">
            {t.unlockProgress}: {unlockProgress}/{required}
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2.5 mb-2">
            <div 
              className="bg-blue-600 h-2.5 rounded-full transition-all" 
              style={{ width: `${(unlockProgress / required) * 100}%` }}
            />
          </div>
          <div className="text-xs text-blue-600">
            {previousTreasureName ? `${t.claimsRemainingFrom} ${previousTreasureName}` : t.claimsRemaining}
          </div>
        </div>
      )}

      {!hideAction && (
        <button
          className={`mt-4 w-full py-2 rounded-lg font-semibold shadow-soft transition-colors ${unlocked ? 'bg-brand-500 hover:bg-brand-600 text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
          disabled={!unlocked}
          onClick={() => unlocked && onBuy && onBuy(id)}
        >
          {unlocked ? t.buyChestButton : t.chestLocked}
        </button>
      )}
    </div>
  );
};

export default TreasureCard;
